// DepositAgreementPage — /send/deposit route: send the move-out deposit-direction
// agreement intake to a past or on-notice tenancy.
//
// Flow:
//   1. List the deposit-eligible occupancies (GET .../occupancies). A toggle
//      controls whether on-notice (not-yet-ended) tenancies are included.
//   2. Each row shows the address, how long ago the lease ended, the signer
//      count, and the resident names.
//   3. "Send deposit agreement" → POST .../initiate sends the recipient-
//      designation intake to everyone on that occupancy.
//
// The backend gates the whole feature behind a flag; when it's off the list call
// answers 503, which the client surfaces as `notEnabled` and we render a friendly
// "not turned on yet" state.

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiError } from '@/lib/api';
import {
  cancelDepositAgreement,
  initiateDepositAgreement,
  listDepositOccupancies,
  type DepositOccupancyRow,
} from '@/lib/depositAgreementClient';
import { isEmptySendContext, sendContextFromSearch } from '@/lib/sendContext';

/** "N days ago" / "today" / "in N days" from a daysSinceLeaseEnd count. */
function endedLabel(days: number): string {
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days === 0) return 'today';
  const ahead = Math.abs(days);
  return `in ${ahead} day${ahead === 1 ? '' : 's'}`;
}

/** Whole days from today to an ISO date (negative when it has passed). */
function daysUntil(iso: string): number {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function DepositAgreementPage(): React.JSX.Element {
  // The household this page was opened FOR (sol finding 10). This surface is
  // keyed on OCCUPANCIES, not on households — there is no field to preselect, so
  // "preselect" here means: widen the list to include on-notice tenancies (a
  // household reached from the list is usually still on notice, and defaulting
  // to Past-only would show an empty page), then mark and scroll to their row.
  // Read once on mount; toggling the checkbox afterwards is the operator's.
  const [searchParams] = useSearchParams();
  const [sendFor] = useState(() => sendContextFromSearch(searchParams));
  const openedForHousehold = !isEmptySendContext(sendFor);
  const [includeNotice, setIncludeNotice] = useState(Boolean(sendFor.unitId));
  const [rows, setRows] = useState<DepositOccupancyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);
  /** Whether the Send action is enabled (the list is read-only and always shown). */
  const [sendEnabled, setSendEnabled] = useState(true);
  /** occupancyIds currently mid-send (disables that row's button). */
  const [sending, setSending] = useState<Set<string>>(new Set());
  /** occupancyIds already sent this session (shows the "sent" state). */
  const [sent, setSent] = useState<Set<string>>(new Set());
  /** occupancyIds currently mid-cancel (disables that row's Cancel button). */
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());
  /** occupancyIds cancelled this session (flips the row back to a fresh send). */
  const [cancelled, setCancelled] = useState<Set<string>>(new Set());
  /** The row awaiting cancel confirmation (null = dialog closed). */
  const [confirmCancel, setConfirmCancel] = useState<DepositOccupancyRow | null>(null);

  const load = useCallback(async (withNotice: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listDepositOccupancies(withNotice);
      setRows(res.occupancies);
      setNotEnabled(res.notEnabled);
      setSendEnabled(res.sendEnabled);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not load occupancies (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not load occupancies';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(includeNotice);
  }, [includeNotice, load]);

  const handleSend = async (row: DepositOccupancyRow) => {
    if (sending.has(row.occupancyId)) return;
    setSending((prev) => new Set(prev).add(row.occupancyId));
    try {
      const res = await initiateDepositAgreement(row.occupancyId);
      const count = res.sent;
      setSent((prev) => new Set(prev).add(row.occupancyId));
      // A re-send after a cancel clears the cancelled state for this row.
      setCancelled((prev) => {
        if (!prev.has(row.occupancyId)) return prev;
        const next = new Set(prev);
        next.delete(row.occupancyId);
        return next;
      });
      toast.success(
        `Sent the recipient-designation intake to ${count} ${count === 1 ? 'person' : 'people'}.`,
        { title: 'Deposit agreement' },
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not send the agreement (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not send the agreement';
      toast.error(msg, { title: 'Deposit agreement' });
    } finally {
      setSending((prev) => {
        const next = new Set(prev);
        next.delete(row.occupancyId);
        return next;
      });
    }
  };

  const handleCancel = async (row: DepositOccupancyRow) => {
    if (cancelling.has(row.occupancyId)) return;
    setCancelling((prev) => new Set(prev).add(row.occupancyId));
    try {
      const res = await cancelDepositAgreement(row.occupancyId);
      setCancelled((prev) => new Set(prev).add(row.occupancyId));
      // A cancel supersedes any this-session "sent" state for the row.
      setSent((prev) => {
        if (!prev.has(row.occupancyId)) return prev;
        const next = new Set(prev);
        next.delete(row.occupancyId);
        return next;
      });
      const n = res.recipientsEmailed;
      toast.success(
        res.alreadyCancelled
          ? 'This deposit agreement was already cancelled.'
          : `Deposit agreement cancelled.${
              res.voidedSubmissionId ? ' The agreement out for signing was voided.' : ''
            }${n > 0 ? ` Notified ${n} ${n === 1 ? 'person' : 'people'}.` : ''}`,
        { title: 'Deposit agreement' },
      );
      setConfirmCancel(null);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not cancel the agreement (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not cancel the agreement';
      toast.error(msg, { title: 'Deposit agreement' });
    } finally {
      setCancelling((prev) => {
        const next = new Set(prev);
        next.delete(row.occupancyId);
        return next;
      });
    }
  };

  /** The rows that ARE the household this page was opened for. */
  const isForSendContext = (row: DepositOccupancyRow): boolean =>
    Boolean(sendFor.unitId) && row.unitId === sendFor.unitId;
  const highlightedCount = rows.filter(isForSendContext).length;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Deposit agreement
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Send the move-out deposit-direction agreement to a tenancy. Each resident and co-signer gets
          a link to designate ONE recipient for any remaining funds; the agreement then renders and
          goes out for signing automatically. Track it on the Requests page.
        </p>
      </div>

      {openedForHousehold && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sending for {sendFor.name ?? 'this household'}</div>
            {sendFor.where ? <div>{sendFor.where}</div> : null}
            <div>
              {!sendFor.unitId
                ? 'Pick their tenancy below.'
                : loading
                  ? 'Finding their tenancy…'
                  : highlightedCount > 0
                    ? 'Their tenancy is marked below.'
                    : 'Their tenancy is not eligible for a deposit agreement yet — it shows up here once the lease is on notice or ended.'}
            </div>
          </div>
        </div>
      )}

      {/* Include-notice control */}
      <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
        <label className="cluster" style={{ gap: 'var(--space-3)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeNotice}
            onChange={(e) => setIncludeNotice(e.target.checked)}
          />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg)' }}>
            Include notice occupancies
            <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
              Show tenancies that are on notice but whose lease hasn&apos;t ended yet.
            </span>
          </span>
        </label>
      </div>

      {/* Sending dormant — the list below is a read-only preview of who qualifies. */}
      {!notEnabled && !sendEnabled ? (
        <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sending isn&apos;t enabled yet</div>
            This is a preview of the tenancies that qualify. The Move-Out Funds Direction Agreement is
            a legal document — once it&apos;s reviewed and the feature is turned on
            (<code>MOVEOUT_FUNDS_SEND_ENABLED</code>), the Send buttons below go live.
          </div>
        </div>
      ) : null}

      {notEnabled ? (
        <div className="alert" data-tone="info" role="note">
          <div className="alert-body">
            The deposit agreement service isn&apos;t available right now. Check back shortly, or ask an
            admin if it persists.
          </div>
        </div>
      ) : error ? (
        <div className="alert" data-tone="danger" role="alert">
          <div className="alert-body">{error}</div>
        </div>
      ) : loading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>Loading occupancies…</p>
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          No {includeNotice ? 'past or on-notice' : 'past'} occupancies are eligible right now.
          {!includeNotice && ' Turn on “Include notice occupancies” to see tenancies still on notice.'}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Address</th>
                <th>Lease ended</th>
                <th>Signers</th>
                <th>Residents</th>
                <th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isSending = sending.has(row.occupancyId);
                const isCancelling = cancelling.has(row.occupancyId);
                // WEL-125: cancelled either this session or on record (server).
                const isCancelled =
                  cancelled.has(row.occupancyId) || Boolean(row.priorSend?.cancelledAt);
                // WEL-121: an RDA is "in flight" when it was sent this session OR a
                // prior RDA is on record — UNLESS it's been cancelled (then the row
                // reverts to a fresh "Send deposit agreement").
                const isSent =
                  !isCancelled &&
                  (sent.has(row.occupancyId) || Boolean(row.priorSend));
                const isTheirs = isForSendContext(row);
                return (
                  <tr
                    key={row.occupancyId}
                    data-active={isTheirs ? 'true' : undefined}
                    ref={
                      isTheirs
                        ? (el) => el?.scrollIntoView({ block: 'center' })
                        : undefined
                    }
                  >
                    <td>
                      <span style={{ color: 'var(--fg)' }}>{row.address}</span>
                      <span
                        className="pill"
                        data-tone={row.status === 'Notice' ? 'warning' : 'neutral'}
                        style={{ marginLeft: 'var(--space-2)' }}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>
                      {row.status === 'Notice' && row.moveOutDate ? (
                        <>
                          <span style={{ color: 'var(--fg)' }}>{formatDate(row.moveOutDate)}</span>
                          <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                            moving out {endedLabel(-daysUntil(row.moveOutDate))}
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{ color: 'var(--fg)' }}>{formatDate(row.leaseEndDate)}</span>
                          <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                            {row.leaseEndDate ? endedLabel(row.daysSinceLeaseEnd) : 'no lease end on file'}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {row.signerCount}
                      {row.cosignerCount > 0 && (
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
                          {' '}({row.cosignerCount} co-signer{row.cosignerCount === 1 ? '' : 's'})
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                      {row.residents.length > 0
                        ? row.residents.map((r) => r.name).join(', ')
                        : '—'}
                    </td>
                    <td className="col-action">
                      <div className="cluster" style={{ gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          data-variant={isSent ? 'secondary' : undefined}
                          onClick={() => void handleSend(row)}
                          disabled={isSending || isCancelling || !sendEnabled}
                          title={!sendEnabled ? 'Sending is not enabled yet' : undefined}
                        >
                          {isSending ? 'Sending…' : isSent ? 'Send again' : 'Send deposit agreement'}
                        </button>
                        {isSent && (
                          <button
                            type="button"
                            className="btn"
                            data-size="sm"
                            data-variant="danger"
                            onClick={() => setConfirmCancel(row)}
                            disabled={isCancelling || isSending || !sendEnabled}
                            title="Cancel this deposit agreement (voids it if it's out for signing)"
                          >
                            {isCancelling ? 'Cancelling…' : 'Cancel'}
                          </button>
                        )}
                      </div>
                      {isCancelled ? (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--danger, var(--fg-muted))', marginTop: 'var(--space-1)' }}>
                          {row.priorSend?.cancelledAt
                            ? `Cancelled ${formatDate(row.priorSend.cancelledAt)}`
                            : 'Cancelled'}
                        </div>
                      ) : row.priorSend ? (
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', marginTop: 'var(--space-1)' }}>
                          {row.priorSend.agreementSentAt
                            ? `Agreement sent ${formatDate(row.priorSend.agreementSentAt)}`
                            : `Intake sent ${formatDate(row.priorSend.initiatedAt)}`}
                          {row.priorSend.round > 1 ? ` · restarted ${row.priorSend.round - 1}×` : ''}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmCancel !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmCancel(null);
        }}
        title="Cancel this deposit agreement?"
        description={
          confirmCancel?.priorSend?.status === 'sent'
            ? 'The agreement that is out for signing will be voided in DocuSeal, and everyone on it gets an email that no action is needed. You can send a fresh one afterward.'
            : 'The intake will be stopped and everyone gets an email that no action is needed. You can send a fresh one afterward.'
        }
        confirmLabel="Cancel agreement"
        busyLabel="Cancelling…"
        tone="danger"
        busy={confirmCancel ? cancelling.has(confirmCancel.occupancyId) : false}
        onConfirm={() => {
          if (confirmCancel) void handleCancel(confirmCancel);
        }}
      />
    </div>
  );
}
